/**
 * Created: 2026-05-05
 * Initial commit: Add coupon templates management page
 *
 * Page for listing, creating, and editing coupon templates for a site.
 */

import { gql } from '@apollo/client'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react'
import { Field, type ISchema, message, useForm } from '@homemaster/jslib'
import AddIcon from '@mui/icons-material/Add'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import EditIcon from '@mui/icons-material/Edit'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import NumberInput from '../components/NumberInput'
import {
  CouponCategory,
  CouponTemplatesDocument,
  type CouponTemplatesQuery,
  CreateCouponTemplateDocument,
  FindUserByMobileDocument,
  IssueCouponDocument,
  SportCategory,
  UpdateCouponTemplateDocument,
} from '../gql/graphql'
import { useSiteContext } from '../hooks'
import { COUPON_CATEGORY_LABEL, SPORT_LABEL } from '../lib/domainLabels'

// Created: 2026-05-05 | Initial commit: Add coupon templates management page
gql`
  query CouponTemplates($siteId: UUID!) {
    couponTemplates(siteId: $siteId) {
      id
      name
      description
      category
      discountAmount
      sport
      validDays
      createdBy
      createTime
    }
  }
`

gql`
  mutation CreateCouponTemplate($templateInput: CreateCouponTemplateInput!) {
    createCouponTemplate(templateInput: $templateInput) {
      id
      name
      description
      category
      discountAmount
      sport
      validDays
      createdBy
      createTime
    }
  }
`

gql`
  mutation UpdateCouponTemplate(
    $couponTemplateId: ID!
    $templateInput: UpdateCouponTemplateInput!
  ) {
    updateCouponTemplate(
      couponTemplateId: $couponTemplateId
      templateInput: $templateInput
    ) {
      id
      name
      description
      category
      discountAmount
      sport
      validDays
    }
  }
`

// Created: 2026-05-05 | Add issue coupon dialog
gql`
  query FindUserByMobile($siteId: UUID!, $mobile: String!) {
    findUserByMobile(siteId: $siteId, mobile: $mobile) {
      ... on UserBasic {
        id
        openid
        name
        avatar
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

gql`
  mutation IssueCoupon($couponTemplateId: ID!, $userId: ID!) {
    issueCoupon(couponTemplateId: $couponTemplateId, userId: $userId) {
      ... on UserCoupon {
        id
        status
        expiresAt
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

type CouponTemplate = NonNullable<CouponTemplatesQuery['couponTemplates']>[number]

interface IssueCouponDialogProps {
  siteId: string
  template: CouponTemplate
  onClose: () => void
}

function IssueCouponDialog({ siteId, template, onClose }: IssueCouponDialogProps) {
  const [mobile, setMobile] = useState('')
  const [foundUser, setFoundUser] = useState<{
    id: string
    name: string | null | undefined
  } | null>(null)
  const [lookupError, setLookupError] = useState('')
  const [issueError, setIssueError] = useState('')
  const [issuing, setIssuing] = useState(false)

  const [findUser, { loading: lookingUp }] = useLazyQuery(FindUserByMobileDocument, {
    fetchPolicy: 'network-only',
  })
  const [issueCoupon] = useMutation(IssueCouponDocument)

  const isMobileValid = /^1\d{10}$/.test(mobile)

  const handleLookup = async () => {
    setFoundUser(null)
    setLookupError('')
    setIssueError('')
    const { data } = await findUser({ variables: { siteId, mobile: mobile.trim() } })
    const result = data?.findUserByMobile
    if (!result) return
    if (result.__typename === 'GraphqlRequestError') {
      setLookupError(result.message ?? '查找失败')
    } else {
      setFoundUser({ id: result.id ?? '', name: result.name })
    }
  }

  const handleIssue = async () => {
    if (!foundUser) return
    setIssuing(true)
    setIssueError('')
    try {
      const { data } = await issueCoupon({
        variables: { couponTemplateId: template.id, userId: foundUser.id },
      })
      const result = data?.issueCoupon
      if (result?.__typename === 'GraphqlRequestError') {
        setIssueError(result.message ?? '发放失败')
        return
      }
      message.success('优惠券已发放', { autoHide: 2000 })
      onClose()
    } catch (err) {
      setIssueError(`发放失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setIssuing(false)
    }
  }

  return (
    <Dialog open onClose={onClose} disableRestoreFocus maxWidth="xs" fullWidth>
      <DialogTitle>发放优惠券 — {template.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="手机号"
              size="small"
              value={mobile}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 11)
                setMobile(digits)
                setFoundUser(null)
                setLookupError('')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isMobileValid) handleLookup()
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              onClick={handleLookup}
              disabled={!isMobileValid || lookingUp}
              sx={{ whiteSpace: 'nowrap', mt: '1px' }}
            >
              {lookingUp ? <CircularProgress size={18} /> : '查找'}
            </Button>
          </Stack>

          {lookupError && (
            <Alert severity="warning" sx={{ py: 0 }}>
              {lookupError}
            </Alert>
          )}

          {foundUser && (
            <Alert severity="success" sx={{ py: 0 }}>
              找到用户：{foundUser.name ?? '（无昵称）'}
            </Alert>
          )}

          {issueError && (
            <Alert severity="error" sx={{ py: 0 }}>
              {issueError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          variant="contained"
          onClick={handleIssue}
          disabled={!foundUser || issuing}
        >
          确认发放
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface CouponFormValues {
  name: string
  description: string
  category: CouponCategory
  discountAmount: number | null
  sport: SportCategory | ''
  validDays: number
}

const schema: ISchema = {
  rules: {
    name: 'required|max:20',
    description: 'max:200',
    validDays: ['required', 'min:1'],
  },
  messages: {
    'required.name': '请填写模板名称',
    'max.name': '名称不能超过20字',
    'max.description': '描述不能超过200字',
    'required.validDays': '请填写有效天数',
    'min.validDays': '有效天数须为正整数',
  },
  validate: ({ category, sport }: CouponFormValues, errors) => {
    if (category !== CouponCategory.FixedDiscount && !sport) {
      errors.add('sport', '请选择运动类型')
    }
  },
}

interface CouponDialogProps {
  siteId: string
  template: CouponTemplate | null
  readonly?: boolean
  onClose: () => void
  onSaved: () => void
}

function CouponDialog({
  siteId,
  template,
  readonly,
  onClose,
  onSaved,
}: CouponDialogProps) {
  const [createTemplate] = useMutation(CreateCouponTemplateDocument)
  const [updateTemplate] = useMutation(UpdateCouponTemplateDocument)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const isEditMode = template != null

  const form = useForm<CouponFormValues>(
    {
      name: template?.name ?? '',
      description: template?.description ?? '',
      category: template?.category ?? CouponCategory.FreeSlot,
      discountAmount: template?.discountAmount ?? null,
      sport: template?.sport ?? '',
      validDays: template?.validDays ?? 60,
    },
    schema,
    readonly,
  )

  const needsDiscount = form.values.category === CouponCategory.FixedDiscount
  const needsSport =
    !isEditMode && form.values.category !== CouponCategory.FixedDiscount

  const handleSubmit = async () => {
    if (form.validate(false)) return

    setSubmitting(true)
    setSubmitError('')
    try {
      const { name, description, category, discountAmount, sport, validDays } =
        form.values
      if (isEditMode) {
        await updateTemplate({
          variables: {
            couponTemplateId: template.id,
            templateInput: {
              name: (name ?? '').trim(),
              description: description.trim() || null,
              validDays: validDays ?? 30,
              discountAmount: needsDiscount ? (discountAmount ?? null) : null,
            },
          },
        })
        message.success('模板已更新', { autoHide: 2000 })
      } else {
        await createTemplate({
          variables: {
            templateInput: {
              siteId,
              name: (name ?? '').trim(),
              description: description.trim() || null,
              category: category as never,
              discountAmount: needsDiscount ? (discountAmount ?? null) : null,
              sport: sport || null,
              validDays: validDays ?? 30,
            },
          },
        })
        message.success('模板已创建', { autoHide: 2000 })
      }
      onSaved()
    } catch (err) {
      setSubmitError(`操作失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose} disableRestoreFocus maxWidth="xs" fullWidth>
      <DialogTitle>
        {isEditMode ? (readonly ? '查看模板' : '编辑模板') : '新建模板'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isEditMode && (
            <FormControl size="small">
              <FormLabel required sx={{ fontSize: '0.75rem' }}>
                类型
              </FormLabel>
              <RadioGroup
                row
                value={form.values.category}
                onChange={(e) => {
                  const newCategory = e.target.value as CouponCategory
                  form.update({ category: newCategory })
                  if (newCategory === CouponCategory.FixedDiscount) {
                    form.update({ discountAmount: null, sport: '' })
                  } else {
                    form.update({ discountAmount: null })
                  }
                }}
              >
                {(
                  [
                    CouponCategory.FreeSlot,
                    CouponCategory.FreeSignup,
                    CouponCategory.FixedDiscount,
                  ] as const
                ).map((cat) => (
                  <FormControlLabel
                    key={cat}
                    value={cat}
                    control={<Radio size="small" />}
                    label={COUPON_CATEGORY_LABEL[cat]}
                    slotProps={{ typography: { variant: 'body2' } }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}

          {needsDiscount && (
            <Field
              form={form}
              name="discountAmount"
              label="折扣金额 (¥)"
              required
              fullWidth
              slotProps={{
                input: {
                  inputComponent: NumberInput,
                  inputProps: {
                    min: 0.01,
                    max: 100,
                    decimalScale: 2,
                    fixedDecimalScale: true,
                  },
                },
              }}
            />
          )}

          {needsSport && (
            <Field select form={form} name="sport" label="适用运动" required fullWidth>
              <MenuItem value={SportCategory.Tennis}>网球</MenuItem>
              <MenuItem value={SportCategory.Pickleball}>匹克球</MenuItem>
            </Field>
          )}

          <Field
            form={form}
            name="validDays"
            label="有效天数"
            required
            fullWidth
            slotProps={{
              input: {
                inputComponent: NumberInput,
                inputProps: { min: 1, max: 365, decimalScale: 0 },
              },
            }}
          />

          <Field
            form={form}
            name="name"
            label="显示名称"
            required
            fullWidth
            inputProps={{ maxLength: 20 }}
          />

          <Field
            form={form}
            name="description"
            label="使用说明"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            inputProps={{ maxLength: 200 }}
            helperText={`${(form.values.description ?? '').length}/200`}
          />

          {submitError && (
            <Typography variant="body2" color="error">
              {submitError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readonly ? '关闭' : '取消'}</Button>
        {!readonly && (
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {isEditMode ? '保存' : '创建'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default function CouponsPage() {
  const { currentSite } = useSiteContext()
  const { data, loading, error, refetch } = useQuery(CouponTemplatesDocument, {
    variables: { siteId: currentSite?.id as string },
    skip: !currentSite,
    fetchPolicy: 'network-only',
  })

  const [dialogTemplate, setDialogTemplate] = useState<
    CouponTemplate | null | undefined
  >(undefined)
  const [issueTemplate, setIssueTemplate] = useState<CouponTemplate | null>(null)

  const handleSaved = async () => {
    await refetch()
    setDialogTemplate(undefined)
  }

  if (loading) return <CircularProgress />
  if (error) return <Alert severity="error">{error.message}</Alert>

  const templates = data?.couponTemplates ?? []

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">优惠券模板</Typography>
        {currentSite?.role === 'SUPER_ADMIN' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogTemplate(null)}
          >
            新建模板
          </Button>
        )}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>名称</TableCell>
              <TableCell>类型</TableCell>
              <TableCell>折扣金额</TableCell>
              <TableCell>运动</TableCell>
              <TableCell>有效天数</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    暂无模板
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {templates.map((t) => (
              <TableRow key={t.id}>
                <TableCell>{t.name}</TableCell>
                <TableCell>{COUPON_CATEGORY_LABEL[t.category] ?? t.category}</TableCell>
                <TableCell>
                  {t.discountAmount != null ? `¥${t.discountAmount}` : '—'}
                </TableCell>
                <TableCell>
                  {t.sport ? (SPORT_LABEL[t.sport] ?? t.sport) : '不限'}
                </TableCell>
                <TableCell>{t.validDays} 天</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    title="发放优惠券"
                    onClick={() => setIssueTemplate(t)}
                  >
                    <CardGiftcardIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDialogTemplate(t)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {dialogTemplate !== undefined && currentSite && (
        <CouponDialog
          siteId={currentSite.id}
          template={dialogTemplate}
          readonly={currentSite.role !== 'SUPER_ADMIN'}
          onClose={() => setDialogTemplate(undefined)}
          onSaved={handleSaved}
        />
      )}

      {issueTemplate && currentSite && (
        <IssueCouponDialog
          siteId={currentSite.id}
          template={issueTemplate}
          onClose={() => setIssueTemplate(null)}
        />
      )}
    </Box>
  )
}
